import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { trackPixelEvent } from '../../components/FacebookPixelTracker';
import { usePricingState } from './usePricingState';
import { createPricingHandlers } from './usePricingHandlers';
import { BillingToggle } from './BillingToggle';
import { PlanCard } from './PlanCard';
import { DowngradeModal } from './DowngradeModal';
import { UpgradeConfirmModal } from './UpgradeConfirmModal';
import { PaidDowngradeConfirmModal } from './PaidDowngradeConfirmModal';
import { DowngradeResourceWizard } from './DowngradeResourceWizard';
import { ErrorToast } from './ErrorToast';
import { UsageCard } from './UsageCard';

export function Pricing({ forceOnboardingFlow = false }: { forceOnboardingFlow?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();
  const isOnboardingFlow = forceOnboardingFlow || location.pathname === '/onboarding';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const state = usePricingState();
  const handlers = createPricingHandlers({
    state,
    navigate,
    isAuthenticated,
    isOnboardingFlow,
  });

  useEffect(() => {
    handlers.loadPricingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    trackPixelEvent('ViewContent', { content_name: 'Pricing' });
  }, []);

  const handleDismissError = () => {
    state.setShowErrorToast(false);
    state.setCheckoutError(null);
  };

  const planChange = state.planChangeModal;
  const pendingPlan =
    planChange != null ? (state.plans.find(p => p.id === planChange.planId) ?? null) : null;

  if (state.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pricing plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back to Dashboard button for authenticated users */}
        {isAuthenticated && !isOnboardingFlow && (
          <div className="mb-6 flex justify-end">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 font-medium transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              <span>Back to Dashboard</span>
            </button>
          </div>
        )}

        {isAuthenticated && isOnboardingFlow && (
          <div className="mb-6 flex justify-end">
            <button
              type="button"
              onClick={handleLogout}
              className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
            >
              Log out
            </button>
          </div>
        )}

        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            {isOnboardingFlow ? 'Finish setting up your account' : 'Choose Your Plan'}
          </h2>
          <p className="mt-4 text-xl text-gray-600">
            {isOnboardingFlow
              ? 'Add a card to continue — the Free plan is $0 today. You will not be charged unless you upgrade to a paid plan.'
              : 'Start building amazing websites with our flexible pricing'}
          </p>
        </div>

        {isOnboardingFlow && !state.plansLoadedFromApi && (
          <div className="mt-6 max-w-3xl mx-auto rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-center text-red-900 text-sm">
            Billing plans could not be loaded. Refresh this page before continuing — a payment
            method is required to access the builder.
          </div>
        )}

        {isAuthenticated &&
          !isOnboardingFlow &&
          state.onboardingStatus?.completed &&
          !state.onboardingStatus.hasPaymentMethod && (
            <div className="mt-6 max-w-3xl mx-auto rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-center text-amber-900">
              Add a payment method to upgrade to a paid plan. You will not be charged until you
              subscribe.
            </div>
          )}

        <BillingToggle
          billingPeriod={state.billingPeriod}
          onBillingPeriodChange={state.setBillingPeriod}
        />

        <div className="mt-12 grid gap-8 lg:grid-cols-3 [&>*]:min-w-0">
          {state.plans.map(plan => {
            const isCurrent = handlers.isCurrentPlan(plan);
            return (
              <PlanCard
                key={plan.id}
                plan={plan}
                billingPeriod={state.billingPeriod}
                isCurrent={isCurrent}
                isUpgrading={state.upgrading === plan.id}
                isWelcomeFlow={isOnboardingFlow}
                checkoutDisabled={isOnboardingFlow && !state.plansLoadedFromApi}
                buttonText={handlers.getButtonText(plan)}
                onUpgrade={handlers.handleUpgrade}
              />
            );
          })}
        </div>

        <UsageCard currentSubscription={state.currentSubscription} />

        <div className="mt-12 text-center">
          <p className="text-gray-600">
            Need help choosing?{' '}
            <a
              href="/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Contact our team
            </a>
          </p>
        </div>
      </div>

      <DowngradeModal
        isOpen={state.showDowngradeModal}
        plans={state.plans}
        currentSubscription={state.currentSubscription}
        downgrading={state.downgrading || state.downgradePreviewLoading}
        downgradeError={state.downgradeError}
        onConfirmDowngrade={handlers.handleConfirmDowngrade}
        onClose={handlers.closeDowngradeModal}
      />

      {state.downgradeResourceWizard && (
        <DowngradeResourceWizard
          isOpen
          config={state.downgradeResourceWizard}
          onClose={handlers.closeDowngradeResourceWizard}
          onResourceCleanupDone={handlers.completeDowngradeResourceWizard}
        />
      )}

      <UpgradeConfirmModal
        isOpen={state.planChangeModal?.kind === 'upgrade'}
        plan={pendingPlan}
        processing={Boolean(
          state.upgrading &&
          state.planChangeModal &&
          state.upgrading === state.planChangeModal.planId
        )}
        onConfirm={handlers.handleConfirmPlanChange}
        onClose={handlers.closePlanChangeModal}
      />

      <PaidDowngradeConfirmModal
        isOpen={state.planChangeModal?.kind === 'paid-downgrade'}
        plan={pendingPlan}
        currentSubscription={state.currentSubscription}
        processing={Boolean(
          state.upgrading &&
          state.planChangeModal &&
          state.upgrading === state.planChangeModal.planId
        )}
        loadingPreview={state.downgradePreviewLoading}
        onConfirm={handlers.handleConfirmPlanChange}
        onClose={handlers.closePlanChangeModal}
      />

      <ErrorToast
        isVisible={state.showErrorToast}
        message={state.checkoutError}
        onDismiss={handleDismissError}
      />
    </div>
  );
}

export default Pricing;
